'use client'

import PropTypes from 'prop-types'
import { useEffect, useState, useCallback } from 'react'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import MUIInput from '@mui/material/TextField'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUITooltip from '@mui/material/Tooltip'
import { formatCurrency } from '@repo/utils/numbers'
import { useDebounce } from '@uidotdev/usehooks'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'

export const TimeRecordingRate = ({
  rateType,
  rateAmount,
  timeAmount,
  quantity,
  timerMinutes,
  onChangeQuantity = () => null,
  index,
  isDisabled
}) => {
  const { timeUnitType, formatTime, fetchRateCalculation } = useTimeRecordingStore()
  const { isOutlookAddin } = useGlobalSettingsStore()
  const [displayQuantity, setDisplayQuantity] = useState('')
  const [typedQuantity, setTypedQuantity] = useState('')
  const debouncedTypedQuantity = useDebounce(typedQuantity, 400)
  const [total, setTotal] = useState('')

  useEffect(() => {
    onChangeQuantity(displayQuantity)
  }, [onChangeQuantity, displayQuantity])

  // calculate total
  useEffect(() => {
    if (isDisabled || !displayQuantity || !rateType || rateType === 'S' || !timeUnitType) {
      setTotal('')
      return
    }

    const getRateCalculation = async () => {
      setTotal('')

      const quantityMeasuresTime = rateType === 'H'

      const total = await fetchRateCalculation({
        rateType,
        rateAmount,
        timeAmount,
        timeQuantity: quantityMeasuresTime ? displayQuantity : null,
        unitQuantity: quantityMeasuresTime ? null : displayQuantity
      })

      const totalAsCurrency = formatCurrency(total, false)
      setTotal(totalAsCurrency)
    }

    getRateCalculation()
  }, [rateAmount, timeAmount, rateType, displayQuantity, isDisabled, timeUnitType, timerMinutes, fetchRateCalculation])

  // handle quantity change
  const setUserQuantity = useCallback(
    (newQuantity, newQuantityIsFromTimerMinutes = false) => {
      if (!timeUnitType || !rateType) return ''

      let newDisplayQuantity = Math.ceil(newQuantity) // round up to nearest whole number

      // convert time to units if necessary
      const displayInUnits = timeUnitType === 'SixMinuteUnits' || rateType === 'U'
      if (displayInUnits && newQuantityIsFromTimerMinutes) {
        newDisplayQuantity = roundUpMinutesToSixMinuteUnits(newQuantity)
      }

      if (timeUnitType === 'RoundUpToSixMinutes' && rateType === 'H') {
        newDisplayQuantity = roundUpMinutesToNearestSixMinutes(newQuantity)
      }

      if (newDisplayQuantity !== displayQuantity) {
        setTotal('')
        setDisplayQuantity(newDisplayQuantity)
      }

      setTypedQuantity('')
    },
    [rateType, timeUnitType, displayQuantity]
  )

  const getFormattedTimerDuration = () => {
    if (!timerMinutes) return ''
    return formatTime(timerMinutes * 60)
  }

  useEffect(() => {
    if (!displayQuantity && index === 0) {
      const defaultQuantity = index === 0 ? timerMinutes || quantity || '' : ''
      if (defaultQuantity) setUserQuantity(defaultQuantity, !!timerMinutes)
    }
  }, [displayQuantity, index, quantity, timerMinutes, setUserQuantity])

  useEffect(() => {
    if (timerMinutes) {
      setUserQuantity(timerMinutes, true)
    }
    // eslint-disable-next-line
  }, [rateType, rateAmount, timeAmount, timeUnitType, timerMinutes]) // deliberately omit setUserQuantity from dependencies

  useEffect(() => {
    if (debouncedTypedQuantity) {
      setUserQuantity(debouncedTypedQuantity)
    }
    // eslint-disable-next-line
  }, [debouncedTypedQuantity]) // deliberately omit setUserQuantity from dependencies

  return (
    <>
      <MUIStack
        data-testid="TimeRecordingRate"
        direction="row"
        gap={isOutlookAddin ? '20px' : '10px'}
        sx={{ width: '100%', mb: 2, flexWrap: isOutlookAddin ? 'wrap' : 'nowrap' }}
      >
        <MUIInput
          value={rateAmount || 0}
          sx={{
            height: '40px',
            minWidth: isOutlookAddin ? '100%' : '150px',
            '.MuiInputBase-root': {
              m: 0,
              height: '40px',
              backgroundColor: 'greyShades.grey1',
              padding: 0,
              pointerEvents: 'none'
            }
          }}
          disabled={isDisabled}
          readOnly
          size="small"
          type="number"
          label="Rate"
          InputProps={{
            startAdornment: <Adornment position="start" content="£" />,
            endAdornment: <Adornment position="end" content={rateType === 'U' ? 'u' : 'hr'} />,
            slotProps: { htmlInput: { min: 0 } }
          }}
        />

        <MUITooltip
          enterDelay={500}
          title={
            timeUnitType === 'SixMinuteUnits' || rateType === 'U'
              ? '1 unit = 6 mins'
              : timeUnitType === 'RoundUpToSixMinutes'
                ? 'Rounded up to nearest 6 mins'
                : ''
          }
          placement="top"
          arrow
        >
          <MUIInput
            value={typedQuantity || displayQuantity || ''}
            sx={{
              height: '40px',
              minWidth: isOutlookAddin ? '100%' : '100px',
              '.MuiInputBase-root': { m: 0, minHeight: '40px', backgroundColor: 'white' }
            }}
            onChange={(e) => setTypedQuantity(e.target.value)}
            disabled={isDisabled}
            required
            type="number"
            size="small"
            label={timeUnitType === 'SixMinuteUnits' || rateType === 'U' ? `Units` : `Minutes`}
          />
        </MUITooltip>

        <MUIInput
          value={total || ''}
          sx={{
            height: '40px',
            minWidth: isOutlookAddin ? '100%' : '150px',
            textAlign: 'right',
            '.MuiInputBase-root': {
              m: 0,
              minHeight: '40px',
              backgroundColor: 'greyShades.grey1',
              paddingLeft: 0,
              pointerEvents: 'none'
            }
          }}
          size="small"
          readOnly
          label="Value"
          InputProps={{
            startAdornment: <Adornment position="start" content="£" />,
            readOnly: true
          }}
        />
      </MUIStack>

      {timerMinutes ? renderNote(`Based on a timer that ran for ${getFormattedTimerDuration()}`) : null}
      {timerMinutes && (timeUnitType === 'SixMinuteUnits' || rateType === 'U')
        ? renderNote(`Minutes have been converted to 6-minute units`)
        : null}
      {timeUnitType === 'RoundUpToSixMinutes' && rateType !== 'U'
        ? renderNote(`Actual time has been rounded up to nearest 6 mins`)
        : null}
    </>
  )
}

const Adornment = ({ position = 'start', content = 'hr' }) => {
  return (
    <MUIInputAdornment position={position}>
      <MUIStack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '40px',
          height: '40px',
          backgroundColor: 'rgba(61, 81, 123, 0.04)',
          userSelect: 'none',
          pointerEvents: 'none',
          borderLeft: position === 'end' ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
          borderRight: position === 'start' ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
          borderRadius: position === 'start' ? '4px 0px 0px 4px' : '0px 4px 4px 0px'
        }}
      >
        {content}
      </MUIStack>
    </MUIInputAdornment>
  )
}

const roundUpMinutesToSixMinuteUnits = (num) => {
  return Math.ceil(num / 6)
}

const roundUpMinutesToNearestSixMinutes = (num) => {
  return Math.ceil(num / 6) * 6
}

const renderNote = (note) => {
  return (
    <MUITypography variant="body2" sx={{ mt: -3, color: 'text.secondary', opacity: 0.85, mb: 0 }}>
      <em>*{note}</em>
    </MUITypography>
  )
}

TimeRecordingRate.propTypes = {
  clientNo: PropTypes.string,
  matterNo: PropTypes.any,
  feeEarnerId: PropTypes.string,
  timeRecordingActivityId: PropTypes.string,
  remunerationTypeId: PropTypes.string,
  onChange: PropTypes.func,
  rateType: PropTypes.string,
  rateAmount: PropTypes.any,
  timeAmount: PropTypes.any,
  quantity: PropTypes.any,
  timerMinutes: PropTypes.number,
  onChangeQuantity: PropTypes.func,
  index: PropTypes.number,
  isDisabled: PropTypes.bool
}

Adornment.propTypes = {
  position: PropTypes.string,
  content: PropTypes.string
}
